import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const NeonGlow = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-neon-glow">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Neon grid lines ── */}
      <svg style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none", opacity: 0.12 }}
        width="420" height="560" viewBox="0 0 420 560">
        {/* vertical lines */}
        {[70, 140, 210, 280, 350].map((x) => (
          <line key={x} x1={x} y1="0" x2={x} y2="560"
            stroke="var(--theme-accent)" strokeWidth="0.5" />
        ))}
        {/* horizontal lines */}
        {[80, 160, 240, 320, 400, 480].map((y) => (
          <line key={y} x1="0" y1={y} x2="420" y2={y}
            stroke="var(--theme-accent)" strokeWidth="0.5" />
        ))}
        {/* Perspective vanishing lines */}
        <line x1="210" y1="560" x2="0"   y2="0" stroke="var(--theme-accent)" strokeWidth="0.5" />
        <line x1="210" y1="560" x2="420" y2="0" stroke="var(--theme-accent)" strokeWidth="0.5" />
      </svg>

      {/* ── Glowing orb behind image ── */}
      <div style={{
        position: "absolute", top: "22%", left: "50%",
        transform: "translate(-50%,-50%)",
        width: 240, height: 240, borderRadius: "50%",
        background: "radial-gradient(circle, var(--theme-accent) 0%, transparent 65%)",
        opacity: 0.18, zIndex: 2, pointerEvents: "none",
        filter: "blur(24px)",
      }} />

      {/* ── Neon border frame ── */}
      <div style={{
        position: "absolute", inset: 8, zIndex: 3, pointerEvents: "none",
        border: "1px solid var(--theme-accent)", borderRadius: 4,
        boxShadow: "0 0 12px var(--theme-accent), inset 0 0 8px rgba(0,0,0,0.3)",
        opacity: 0.35,
      }} />
      <div style={{
        position: "absolute", inset: 11, zIndex: 3, pointerEvents: "none",
        border: "1px solid var(--theme-accent)", borderRadius: 3,
        opacity: 0.12,
      }} />

      {/* ── Corner bracket ornaments ── */}
      {[
        { top: 12, left: 12 },
        { top: 12, right: 12 },
        { bottom: 12, left: 12 },
        { bottom: 12, right: 12 },
      ].map((pos, i) => {
        const isRight = pos.right !== undefined;
        const isBottom = pos.bottom !== undefined;
        return (
          <svg key={i} style={{ position: "absolute", ...pos, zIndex: 8, opacity: 0.8 }}
            width="14" height="14" viewBox="0 0 14 14">
            <path
              d={isRight
                ? (isBottom ? "M14,4 L14,14 L4,14" : "M14,10 L14,0 L4,0")
                : (isBottom ? "M0,4  L0,14 L10,14" : "M0,10 L0,0  L10,0")}
              stroke="var(--theme-accent)" strokeWidth="2"
              fill="none" strokeLinecap="square"
            />
          </svg>
        );
      })}

      {/* ── Scan line accent bar ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: "linear-gradient(90deg, transparent, var(--theme-accent), transparent)",
        zIndex: 10, opacity: 0.9,
      }} />

      {/* ── Content ── */}
      <div style={{
        position: "relative", zIndex: 5,
        display: "flex", flexDirection: "column",
        height: "100%", padding: "16px 22px 14px",
      }}>

        {/* ── Brand row ── */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 6,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {isVisible("logoUrl") && buyer?.logoUrl && (
              <img src={buyer.logoUrl} alt="Logo"
                style={{ width: 22, height: 22, objectFit: "contain", borderRadius: 2 }} />
            )}
            {isVisible("organizationName") && (
              <span style={{
                fontFamily: "var(--theme-heading-font,'Orbitron','Rajdhani',sans-serif)",
                fontSize: "0.72rem", fontWeight: 800,
                color: "var(--theme-text)",
                textTransform: "uppercase", letterSpacing: "0.2em",
                textShadow: "0 0 8px var(--theme-accent)",
              }}>
                {(buyer?.organizationName || "NEXUS").toUpperCase()}
              </span>
            )}
          </div>
          {/* glowing pill badge */}
          <div style={{
            background: "transparent",
            border: "1px solid var(--theme-accent)",
            color: "var(--theme-accent)",
            fontSize: "0.5rem", fontWeight: 800,
            padding: "3px 8px", borderRadius: 20, letterSpacing: "0.15em",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            boxShadow: "0 0 6px var(--theme-accent)",
          }}>
            {product?.segment?.toUpperCase() || "JEWELLERY"}
          </div>
        </div>

        {/* ── Headline ── */}
        <div style={{ textAlign: "center", marginBottom: 4 }}>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Orbitron','Rajdhani',sans-serif)",
            fontSize: "0.6rem", fontWeight: 400, letterSpacing: "0.35em",
            color: "var(--theme-accent)", textTransform: "uppercase",
          }}>
            NEW ARRIVAL
          </div>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "1.3rem", fontWeight: 900,
            color: "var(--theme-text)", letterSpacing: "0.06em",
            lineHeight: 1.15,
            textShadow: "0 0 18px var(--theme-accent)",
          }}>
            {product?.name || "FINE JEWELLERY"}
          </div>
        </div>

        {/* ── Separator ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6, marginBottom: 6,
        }}>
          <div style={{ flex: 1, height: 1, background: "var(--theme-accent)", opacity: 0.35 }} />
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,4 4,8 0,4"
              fill="var(--theme-accent)" opacity="0.8" />
          </svg>
          <div style={{ flex: 1, height: 1, background: "var(--theme-accent)", opacity: 0.35 }} />
        </div>

        {/* ── Product image ── */}
        <div style={{
          position: "relative",
          flex: "0 0 180px",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 6,
        }}>
          <img
            src={Array.isArray(product?.image_url) ? product.image_url[0] : product?.image_url}
            alt={product?.name || ""}
            crossOrigin="anonymous"
            style={{
              maxWidth: "70%", maxHeight: "100%", objectFit: "contain",
              position: "relative", zIndex: 2,
              filter: "drop-shadow(0 0 16px var(--theme-accent)) drop-shadow(0 8px 20px rgba(0,0,0,0.5))",
            }}
          />
        </div>

        {/* ── Attribute rows (horizontal list) ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "3px 10px",
            background: "var(--theme-surface)",
            border: "1px solid var(--theme-accent)",
            borderRadius: 2, padding: "6px 10px", marginBottom: 8,
            opacity: 0.95,
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.47rem", fontWeight: 700,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <DiamondShapeSVG shapeName={String(v)} size={22} color="var(--theme-accent)" />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600, color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>{fmtVal(v)}</span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.64rem", fontWeight: 600, color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>{fmtVal(v)}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* ── Price + CTA ── */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid var(--theme-accent)",
          borderRadius: 3, padding: "8px 14px", marginBottom: 7,
          background: "var(--theme-surface)",
          boxShadow: "0 0 10px var(--theme-accent)",
          opacity: 0.95,
        }}>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "1.1rem", fontWeight: 900,
            color: "var(--theme-accent)",
            textShadow: "0 0 10px var(--theme-accent)",
          }}>
            {formatPrice(price)}
          </div>
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.12em",
            padding: "6px 14px", borderRadius: 2,
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            ◆ BUY NOW
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid var(--theme-border)", paddingTop: 5,
          fontSize: "0.5rem", color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          letterSpacing: "0.08em",
        }}>
          {isVisible("address") && <span>{buyer?.streetAddress || "123 Jewellers Lane"}</span>}
          {isVisible("phone") && <span>{buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>}
        </div>
      </div>
    </div>
  );
});

NeonGlow.displayName = "NeonGlow";
export default NeonGlow;
